import { PlanType, requestConfirmationEmail } from '@otter-ai/recall-client';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { PATH_URL } from '#constants';
import { loginInfoQueryCreator } from '#query';
import { CreateUserType, NEW_ONBOARDING_FLOW, OtterPlanType } from '#types';
import { buildQueryParams, setPartialLoginState, useExperiment } from '#utils';

interface UsePurchaseRedirectParams {
  planType: PlanType;
  redirectUrl?: string;
}

export const usePurchaseRedirect = (params: UsePurchaseRedirectParams) => {
  const searchParams = useSearchParams();

  const { data: loginData } = useQuery(loginInfoQueryCreator());
  const expDataPro = useExperiment('post_purchase_pro_v2', {
    enabled: loginData?.['logged-in'] === true,
  });
  const expDataBiz = useExperiment('post_purchase_biz_v2', {
    enabled: loginData?.['logged-in'] === true,
  });

  const getBaseParams = (
    planType: PlanType,
    experiments?: CreateUserType['experiments'],
  ) => {
    const proGroup = experiments
      ? experiments.find(
          (experiment) => experiment.experiment === 'post_purchase_pro_v2',
        )?.group
      : expDataPro.group;
    const isTreatmentPro = proGroup === 'treatment';

    const bizGroup = experiments
      ? experiments.find(
          (experiment) => experiment.experiment === 'post_purchase_biz_v2',
        )?.group
      : expDataBiz.group;
    const isTreatmentBiz = bizGroup === 'treatment';
    const isHpPromoOrEDU =
      searchParams.get('redeem_token') || searchParams.get('type') === 'edu';

    if (
      !isHpPromoOrEDU &&
      ((planType === OtterPlanType.PREMIUM && isTreatmentPro) ||
        (planType === OtterPlanType.TEAM && isTreatmentBiz))
    ) {
      return { new_onboarding: NEW_ONBOARDING_FLOW.POST_PURCHASE_V2 };
    }
    if (planType === OtterPlanType.PREMIUM) {
      return { new_onboarding: NEW_ONBOARDING_FLOW.GENERAL_EMAIL };
    }
    if (planType === OtterPlanType.TEAM) {
      const deptValue = searchParams.get('dept') ?? '';
      return {
        new_onboarding: NEW_ONBOARDING_FLOW.WORKSPACE_ADMIN,
        ...(deptValue ? { dept: deptValue } : {}),
      };
    }

    return { new_onboarding: NEW_ONBOARDING_FLOW.GENERAL_EMAIL };
  };

  const getQueryParamString = (
    planType: PlanType,
    experiments?: CreateUserType['experiments'],
  ) => buildQueryParams(getBaseParams(planType, experiments));

  const getOnboardingRedirectUrl = (planType: PlanType) => {
    const params = getBaseParams(planType);
    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return `/welcome${queryString ? `?${queryString}` : ''}`;
  };

  const isValidConversationUrl = (url: string): boolean => {
    // Handle both with and without leading slash
    const conversationPattern = new RegExp(
      `^/?${PATH_URL.CONVERSATION_PAGE}/[a-zA-Z0-9_-]+$`,
    );
    return conversationPattern.test(url);
  };

  const handlePurchaseSuccessRedirect = async () => {
    // Handle redirect to conversation page if r parameter is a valid conversation url
    if (params?.redirectUrl && isValidConversationUrl(params.redirectUrl)) {
      const redirectUrl = params.redirectUrl.startsWith('/')
        ? params.redirectUrl
        : `/${params.redirectUrl}`;

      setPartialLoginState({
        redirectUrl: redirectUrl,
      });
      window.location.href = redirectUrl;
      return;
    }

    const onboardingRedirectUrl = getOnboardingRedirectUrl(params.planType);
    setPartialLoginState({
      redirectUrl: onboardingRedirectUrl,
    });
    window.location.href = onboardingRedirectUrl;
  };

  const requestEmail = async (
    userEmail: string,
    experiments?: CreateUserType['experiments'],
  ) => {
    await requestConfirmationEmail({
      query: {
        query_params: getQueryParamString(params.planType, experiments),
      },
    });
    setPartialLoginState({ confirmationEmailSent: userEmail });
  };

  return {
    handlePurchaseSuccessRedirect,
    requestEmail,
  };
};
